/**
 * ProgressSummaryPanel — Phase 5
 *
 * Surfaces a "Summarise progress notes" inline button on the Participant
 * Chart Overview. Pulls recent notes from `/api/notes/by-participant/{id}`,
 * lets the coordinator pick 30/60/90 days, then routes the notes through
 * `/api/ai-copilot/draft` with feature=`progress_summary`. AI output is
 * draft-only — review dialog handles accept/reject.
 *
 * Renders nothing when `care_plan_ai_assist_enabled` is OFF (parent should
 * still feature-gate the import for consistency, but this is an extra
 * fail-closed guard inside the component itself).
 */
import React, { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AISuggestButton, useAICopilotEnabled } from '@/components/ai/AISuggestButton';
import { AIDraftReviewDialog } from '@/components/ai/AIDraftReviewDialog';

const PERIODS = [30, 60, 90];

export function ProgressSummaryPanel({ participantId }) {
  const enabled = useAICopilotEnabled();
  const [period, setPeriod] = useState(30);
  const [draft, setDraft] = useState(null);
  const [acceptedSummary, setAcceptedSummary] = useState(null);

  if (!enabled) return null;

  return (
    <Card data-testid="ai-progress-summary-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <h3 className="font-semibold text-sm">AI Progress Summary</h3>
          </div>
          <div className="flex items-center gap-1">
            {PERIODS.map(p => (
              <Button
                key={p} size="sm"
                variant={period === p ? 'default' : 'outline'}
                onClick={() => setPeriod(p)}
                data-testid={`ai-progress-period-${p}`}
              >
                {p}d
              </Button>
            ))}
            <AISuggestButton
              feature="progress_summary"
              label="Summarise"
              testid="ai-progress-summarise"
              buildPayload={async () => {
                try {
                  const r = await api.get(`/notes/by-participant/${participantId}`);
                  const all = r.data.items || r.data.data || [];
                  const cutoff = new Date();
                  cutoff.setDate(cutoff.getDate() - period);
                  const inWindow = all.filter(n => {
                    const d = new Date(n.created_at || n.shift_date || n.date);
                    return !isNaN(d) && d >= cutoff;
                  }).map(n => ({
                    date: (n.shift_date || n.created_at || '').slice(0, 10),
                    note: n.text || n.note || n.content || '',
                  }));
                  if (inWindow.length === 0) {
                    toast.info(`No progress notes found in the last ${period} days.`);
                    return null;
                  }
                  return {
                    participant_id: participantId,
                    period_days: period,
                    notes: inWindow,
                    participant_ctx: { participant_id: participantId },
                  };
                } catch (e) {
                  toast.error('Failed to load progress notes.');
                  return null;
                }
              }}
              onDraft={(env) => setDraft(env)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Picks the last <strong>{period}</strong> days of progress notes for this participant and asks
          the AI Copilot to produce a draft summary. Nothing is saved until you accept.
        </p>

        {acceptedSummary && (
          <div className="border rounded p-2 text-xs space-y-1" data-testid="ai-progress-accepted">
            <p className="font-semibold">Last accepted summary ({acceptedSummary.period_days || period}d):</p>
            <p>{acceptedSummary.headline}</p>
          </div>
        )}
      </CardContent>

      <AIDraftReviewDialog
        open={!!draft}
        onOpenChange={(v) => !v && setDraft(null)}
        draftEnvelope={draft}
        participantId={participantId}
        targetEntity="progress_summary"
        onAccept={(d) => setAcceptedSummary(d)}
      />
    </Card>
  );
}
